import {
  ApproveOrderCandidatePayload,
  BuyBackPurchaseOrderPayload,
  CancelPurchaseOrderPayload,
  CreateAdminQuotePayload,
  CreateAdminQuoteResult,
  CreateInvoicePayload,
  CreatePurchaseOrderManualPayload,
  CreateSalesOrderManualPayload,
  DeleteInvoicePayload,
  DeleteInventoryPayload,
  DeletePaymentPayload,
  DealDetails,
  FunctionsService,
  GetDealsByIdsPayload,
  GlobalSearchPayload,
  GlobalSearchResult,
  RemoveBlockedContactPayload,
  RemoveBlockedContactResult,
  UpsertBlockedContactPayload,
  UpsertBlockedContactResult,
  GetMonthlyProductReportByProductPayload,
  GetProductReportByTimeRangePayload,
  GetPurchaseOrderReportPayload,
  GetSalesOrderReportPayload,
  LogSensitiveRevealPayload,
  MercuryBalance,
  AirwallexBalance,
  ResolveAirwallexPaymentMatchPayload,
  PurchaseOrderReportRow,
  SalesReportRow,
  RecordAccountIssuePayload,
  RecordPaymentPayload,
  RecordStockCheckPayload,
  RejectOrderCandidatePayload,
  ReportData,
  ResolveMercuryPaymentMatchPayload,
  ResolvePaypalPaymentMatchPayload,
  UpdateOrderCandidatePayload,
  UpdatePaymentStatusPayload,
  UnlinkPurchaseOrderPayload,
  UpdatePurchaseOrderPayload,
  UpdateSalesOrderPayload,
  UpdateInvoicePayload,
} from "@/core";
import { firebase } from "@/infrastructure";
import { httpsCallable } from "@firebase/functions";

export const functionsService: FunctionsService = {
  async getProductReportByTimeRange(
    payload: GetProductReportByTimeRangePayload,
  ) {
    const result = await httpsCallable<
      GetProductReportByTimeRangePayload,
      ReportData[]
    >(
      firebase.functions,
      "getProductReportByTimeRange",
    )(payload);
    return result.data;
  },
  async getMonthlyProductReportByProduct(
    payload: GetMonthlyProductReportByProductPayload,
  ) {
    const result = await httpsCallable<
      GetMonthlyProductReportByProductPayload,
      ReportData[]
    >(
      firebase.functions,
      "getMonthlyProductReportByProduct",
    )(payload);
    return result.data;
  },
  async getPurchaseOrderReport(payload: GetPurchaseOrderReportPayload) {
    const result = await httpsCallable<
      GetPurchaseOrderReportPayload,
      PurchaseOrderReportRow[]
    >(
      firebase.functions,
      "getPurchaseOrderReport",
    )(payload);
    return result.data;
  },
  async getSalesOrderReport(payload: GetSalesOrderReportPayload) {
    const result = await httpsCallable<
      GetSalesOrderReportPayload,
      SalesReportRow[]
    >(
      firebase.functions,
      "getSalesOrderReport",
    )(payload);
    return result.data;
  },
  async getDealsByIds(payload: GetDealsByIdsPayload) {
    const result = await httpsCallable<
      GetDealsByIdsPayload,
      DealDetails[]
    >(
      firebase.functions,
      "getDealsByIds",
    )(payload);
    return result.data;
  },
  async approveOrderCandidate(payload: ApproveOrderCandidatePayload) {
    const result = await httpsCallable<
      ApproveOrderCandidatePayload,
      {
        convertedOrderId: string;
        candidateType: "po" | "so";
        inventoryId?: string;
      }
    >(
      firebase.functions,
      "approveOrderCandidate",
    )(payload);
    return result.data;
  },
  async rejectOrderCandidate(payload: RejectOrderCandidatePayload) {
    const result = await httpsCallable<
      RejectOrderCandidatePayload,
      { success: boolean }
    >(
      firebase.functions,
      "rejectOrderCandidate",
    )(payload);
    return result.data;
  },
  async updateOrderCandidate(payload: UpdateOrderCandidatePayload) {
    const result = await httpsCallable<
      UpdateOrderCandidatePayload,
      { success: boolean }
    >(
      firebase.functions,
      "updateOrderCandidate",
    )(payload);
    return result.data;
  },
  async createPurchaseOrderManual(payload: CreatePurchaseOrderManualPayload) {
    const result = await httpsCallable<
      CreatePurchaseOrderManualPayload,
      { poId: string; inventoryId: string }
    >(
      firebase.functions,
      "createPurchaseOrderManual",
    )(payload);
    return result.data;
  },
  async createSalesOrderManual(payload: CreateSalesOrderManualPayload) {
    const result = await httpsCallable<
      CreateSalesOrderManualPayload,
      { soId: string; inventoryId: string; status: "draft" | "allocated" }
    >(
      firebase.functions,
      "createSalesOrderManual",
    )(payload);
    return result.data;
  },
  async updatePurchaseOrder(payload: UpdatePurchaseOrderPayload) {
    const result = await httpsCallable<
      UpdatePurchaseOrderPayload,
      { success: boolean }
    >(
      firebase.functions,
      "updatePurchaseOrder",
    )(payload);
    return result.data;
  },
  async updateSalesOrder(payload: UpdateSalesOrderPayload) {
    const result = await httpsCallable<
      UpdateSalesOrderPayload,
      { success: boolean }
    >(
      firebase.functions,
      "updateSalesOrder",
    )(payload);
    return result.data;
  },
  async recordStockCheck(payload: RecordStockCheckPayload) {
    const result = await httpsCallable<
      RecordStockCheckPayload,
      { stockCheckId: string; variance: number }
    >(
      firebase.functions,
      "recordStockCheck",
    )(payload);
    return result.data;
  },
  async recordAccountIssue(payload: RecordAccountIssuePayload) {
    const result = await httpsCallable<
      RecordAccountIssuePayload,
      { accountIssueId: string }
    >(
      firebase.functions,
      "recordAccountIssue",
    )(payload);
    return result.data;
  },
  async createInvoice(payload: CreateInvoicePayload) {
    const result = await httpsCallable<
      CreateInvoicePayload,
      { invoiceId: string }
    >(
      firebase.functions,
      "createInvoice",
    )(payload);
    return result.data;
  },
  async updateInvoice(payload: UpdateInvoicePayload) {
    const result = await httpsCallable<
      UpdateInvoicePayload,
      { success: boolean }
    >(
      firebase.functions,
      "updateInvoice",
    )(payload);
    return result.data;
  },
  async deleteInvoice(payload: DeleteInvoicePayload) {
    const result = await httpsCallable<
      DeleteInvoicePayload,
      { success: boolean }
    >(
      firebase.functions,
      "deleteInvoice",
    )(payload);
    return result.data;
  },
  async recordPayment(payload: RecordPaymentPayload) {
    const result = await httpsCallable<
      RecordPaymentPayload,
      { paymentId: string }
    >(
      firebase.functions,
      "recordPayment",
    )(payload);
    return result.data;
  },
  async buyBackPurchaseOrder(payload: BuyBackPurchaseOrderPayload) {
    const result = await httpsCallable<
      BuyBackPurchaseOrderPayload,
      { success: boolean; voided: boolean; paymentId: string }
    >(
      firebase.functions,
      "buyBackPurchaseOrder",
    )(payload);
    return result.data;
  },
  async cancelPurchaseOrder(payload: CancelPurchaseOrderPayload) {
    const result = await httpsCallable<
      CancelPurchaseOrderPayload,
      { success: boolean; voidedInvoiceIds: string[] }
    >(
      firebase.functions,
      "cancelPurchaseOrder",
    )(payload);
    return result.data;
  },
  async unlinkPurchaseOrder(payload: UnlinkPurchaseOrderPayload) {
    const result = await httpsCallable<
      UnlinkPurchaseOrderPayload,
      { success: boolean }
    >(
      firebase.functions,
      "unlinkPurchaseOrder",
    )(payload);
    return result.data;
  },
  async deletePayment(payload: DeletePaymentPayload) {
    const result = await httpsCallable<
      DeletePaymentPayload,
      { success: boolean }
    >(
      firebase.functions,
      "deletePayment",
    )(payload);
    return result.data;
  },
  async updatePaymentStatus(payload: UpdatePaymentStatusPayload) {
    const result = await httpsCallable<
      UpdatePaymentStatusPayload,
      { success: boolean }
    >(
      firebase.functions,
      "updatePaymentStatus",
    )(payload);
    return result.data;
  },
  async resolvePaypalPaymentMatch(payload: ResolvePaypalPaymentMatchPayload) {
    const result = await httpsCallable<
      ResolvePaypalPaymentMatchPayload,
      { success: boolean; matchStatus: string }
    >(
      firebase.functions,
      "resolvePaypalPaymentMatch",
    )(payload);
    return result.data;
  },
  async resolveMercuryPaymentMatch(payload: ResolveMercuryPaymentMatchPayload) {
    const result = await httpsCallable<
      ResolveMercuryPaymentMatchPayload,
      { success: boolean; matchStatus: string }
    >(
      firebase.functions,
      "resolveMercuryPaymentMatch",
    )(payload);
    return result.data;
  },
  async getMercuryBalances() {
    const result = await httpsCallable<
      void,
      { balances: MercuryBalance[] }
    >(
      firebase.functions,
      "getMercuryBalances",
    )();
    return result.data.balances;
  },
  async resolveAirwallexPaymentMatch(
    payload: ResolveAirwallexPaymentMatchPayload,
  ) {
    const result = await httpsCallable<
      ResolveAirwallexPaymentMatchPayload,
      { success: boolean; matchStatus: string }
    >(
      firebase.functions,
      "resolveAirwallexPaymentMatch",
    )(payload);
    return result.data;
  },
  async getAirwallexBalances() {
    const result = await httpsCallable<
      void,
      { balances: AirwallexBalance[] }
    >(
      firebase.functions,
      "getAirwallexBalances",
    )();
    return result.data.balances;
  },
  async deleteInventory(payload: DeleteInventoryPayload) {
    const result = await httpsCallable<
      DeleteInventoryPayload,
      { success: boolean }
    >(
      firebase.functions,
      "deleteInventory",
    )(payload);
    return result.data;
  },
  async logSensitiveReveal(payload: LogSensitiveRevealPayload) {
    const result = await httpsCallable<
      LogSensitiveRevealPayload,
      { success: boolean }
    >(
      firebase.functions,
      "logSensitiveReveal",
    )(payload);
    return result.data;
  },
  async upsertBlockedContact(payload: UpsertBlockedContactPayload) {
    const result = await httpsCallable<
      UpsertBlockedContactPayload,
      UpsertBlockedContactResult
    >(
      firebase.functions,
      "upsertBlockedContact",
    )(payload);
    return result.data;
  },
  async removeBlockedContact(payload: RemoveBlockedContactPayload) {
    const result = await httpsCallable<
      RemoveBlockedContactPayload,
      RemoveBlockedContactResult
    >(
      firebase.functions,
      "removeBlockedContact",
    )(payload);
    return result.data;
  },
  async createAdminQuote(payload: CreateAdminQuotePayload) {
    const result = await httpsCallable<
      CreateAdminQuotePayload,
      CreateAdminQuoteResult
    >(
      firebase.functions,
      "createAdminQuote",
    )(payload);
    return result.data;
  },
  async globalSearch(payload: GlobalSearchPayload) {
    const result = await httpsCallable<GlobalSearchPayload, GlobalSearchResult>(
      firebase.functions,
      "globalSearch",
    )(payload);
    return result.data;
  },
};
